/**
 * Language switcher — two variants:
 *   "sidebar"  → styled dropdown panel (button + animated listbox), used in Sidebar footer
 *   "topbar"   → compact dropdown (flag chip + chevron), used in MobileTopBar
 *   "settings" → the original bare <select> for the Settings page (unchanged behaviour)
 *
 * Persistence logic is identical in all variants: initI18n + cookie + POST /user/preferences,
 * optimistic update with revert on failure.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import {
  SUPPORTED_LOCALES,
  LOCALE_DISPLAY,
  isSupportedLocale,
  type Locale,
} from "@a2/i18n/config";
import { initI18n, currentLocale } from "../lib/i18n";
import { authedFetch } from "../lib/api-client";
import { useT } from "../lib/use-t";

// ── Flag chips ────────────────────────────────────────────────────────────────

/** Three-band CSS flag for NL */
function FlagNL({ size = 20 }: { size?: number }) {
  const h = Math.round(size * 0.7);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        flexDirection: "column",
        width: size,
        height: h,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,.12)",
        flexShrink: 0,
      }}
    >
      <span style={{ flex: 1, background: "#c8102e" }} />
      <span style={{ flex: 1, background: "#fff" }} />
      <span style={{ flex: 1, background: "#21468b" }} />
    </span>
  );
}

/** Union-Jack CSS flag for GB/EN */
function FlagGB({ size = 20 }: { size?: number }) {
  const h = Math.round(size * 0.7);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        position: "relative",
        width: size,
        height: h,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,.12)",
        flexShrink: 0,
        background: "#1a3a78",
      }}
    >
      {/* White diagonals */}
      <span
        style={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(to bottom right, transparent 43%, #fff 43%, #fff 57%, transparent 57%),
            linear-gradient(to bottom left,  transparent 43%, #fff 43%, #fff 57%, transparent 57%)
          `,
        }}
      />
      {/* Red cross */}
      <span
        style={{
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(#c8102e,#c8102e) center / 100% 30% no-repeat,
            linear-gradient(#c8102e,#c8102e) center / 30% 100% no-repeat
          `,
        }}
      />
    </span>
  );
}

/** Generic flag chip: uses emoji flag for locales other than NL + EN */
function FlagChip({ locale, size = 20 }: { locale: Locale; size?: number }) {
  if (locale === "nl") return <FlagNL size={size} />;
  if (locale === "en") return <FlagGB size={size} />;
  // Emoji flag fallback — acceptable per brand.md for non-NL/EN locales
  return (
    <span aria-hidden="true" style={{ fontSize: size * 0.75, lineHeight: 1 }}>
      {LOCALE_DISPLAY[locale].flag}
    </span>
  );
}

// ── Shared persistence hook ───────────────────────────────────────────────────

function useLocale() {
  const [active, setActive] = useState<Locale>(currentLocale());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = useCallback(
    async (next: Locale) => {
      if (next === active) return;
      setPending(true);
      setError(null);
      const prev = active;
      try {
        await initI18n(next);
        document.cookie = `a2easy_lang=${next}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
        setActive(next);
        const res = await authedFetch("/user/preferences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ preferredLanguage: next }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (e) {
        setError(`Failed to save: ${(e as Error).message}`);
        await initI18n(prev);
        setActive(prev);
      } finally {
        setPending(false);
      }
    },
    [active],
  );

  return { active, pending, error, pick };
}

// ── Dropdown component (sidebar + topbar variants) ────────────────────────────

type DropdownVariant = "sidebar" | "topbar";

function LanguageDropdown({ variant }: { variant: DropdownVariant }) {
  const { active, pending, error, pick } = useLocale();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [open]);

  const isSidebar = variant === "sidebar";
  const flagSize = isSidebar ? 20 : 18;
  const currentDisplay = LOCALE_DISPLAY[active];

  return (
    <div ref={containerRef} style={{ position: "relative", userSelect: "none" }}>
      {/* Trigger button */}
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={pending}
        onClick={() => setOpen((v) => !v)}
        className={[
          "inline-flex items-center gap-2 font-semibold text-[13.5px] text-[var(--ink)]",
          "bg-[var(--panel)] border border-[var(--border-strong)] cursor-pointer",
          "transition-colors duration-150 hover:bg-[var(--panel-soft)]",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
          "disabled:opacity-50",
          isSidebar
            ? "w-full rounded-[var(--radius)] px-3 py-[9px] min-h-[40px]"
            : "rounded-[11px] px-[11px] py-[9px] min-h-[40px]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <FlagChip locale={active} size={flagSize} />
        <span>{(currentDisplay.bcp47.split("-")[0] ?? currentDisplay.bcp47).toUpperCase()}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          aria-hidden="true"
          style={{
            marginLeft: isSidebar ? "auto" : 2,
            color: "var(--ink-faint)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-out",
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Dropdown menu */}
      <div
        role="listbox"
        aria-label="Taal kiezen"
        style={{
          position: "absolute",
          // sidebar: open upward above the button; topbar: open downward
          ...(isSidebar
            ? { bottom: "calc(100% + 8px)", left: 0, right: 0 }
            : { top: "calc(100% + 8px)", right: 0, minWidth: 180 }),
          background: "var(--panel)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          boxShadow:
            "0 1px 2px rgba(0,0,0,.06), 0 18px 38px -16px rgba(0,0,0,.34)",
          padding: 6,
          zIndex: 20,
          opacity: open ? 1 : 0,
          transform: open ? "none" : "translateY(-6px) scale(0.98)",
          transformOrigin: isSidebar ? "bottom center" : "top right",
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
        }}
      >
        {SUPPORTED_LOCALES.map((loc) => {
          const display = LOCALE_DISPLAY[loc];
          const isSelected = loc === active;
          return (
            <button
              key={loc}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                void pick(loc);
                setOpen(false);
              }}
              className={[
                "flex items-center gap-2.5 w-full border-0 bg-transparent cursor-pointer",
                "font-medium text-sm text-[var(--ink)] px-[11px] py-[10px] rounded-[9px] text-left",
                "hover:bg-[var(--panel-soft)]",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-[-2px]",
                "min-h-[44px]",
                isSelected ? "text-[var(--accent)]" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <FlagChip locale={loc} size={18} />
              <span>{display.endonym}</span>
              {/* Checkmark */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{
                  marginLeft: "auto",
                  color: "var(--accent)",
                  opacity: isSelected ? 1 : 0,
                }}
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </button>
          );
        })}
      </div>

      {error ? (
        <p className="text-xs text-[var(--fail)] mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Settings page variant (original <select>) ─────────────────────────────────

function LanguageSelect() {
  const t = useT();
  const { active, pending, error, pick } = useLocale();

  return (
    <div className="space-y-2">
      <label className="block text-sm text-[var(--ink-muted)]" htmlFor="lang-select">
        {t("settings.languageLabel")}
      </label>
      <select
        id="lang-select"
        value={active}
        disabled={pending}
        onChange={(e) => {
          const v = e.target.value;
          if (isSupportedLocale(v)) void pick(v);
        }}
        className="min-h-11 rounded-[var(--radius)] bg-[var(--panel)] border border-[var(--border-strong)] px-3 py-2.5 text-sm text-[var(--ink)] transition-colors hover:bg-[var(--panel-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
      >
        {SUPPORTED_LOCALES.map((loc) => (
          <option key={loc} value={loc}>
            {LOCALE_DISPLAY[loc].flag} {LOCALE_DISPLAY[loc].endonym}
          </option>
        ))}
      </select>
      {error ? (
        <p className="text-xs text-[var(--fail)]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────

export type LanguageSwitcherVariant = "sidebar" | "topbar" | "settings";

export function LanguageSwitcher({
  variant = "settings",
}: {
  variant?: LanguageSwitcherVariant;
}) {
  if (variant === "settings") return <LanguageSelect />;
  return <LanguageDropdown variant={variant} />;
}
